import type {
  UploadedDocument,
  KnowledgeSource,
  TaskDefinition,
  ConnectedTool,
  FleetConnection,
  CapabilityToggle,
  ConfigChatMessage,
  SuggestionChipSet,
  SpecialistPrompt,
  OutputFormatInfo,
} from './configureTypes';

export const mockDocuments: UploadedDocument[] = [
  {
    id: 'doc-1',
    name: 'Menu-Spring-2026.pdf',
    type: 'PDF',
    size: '2.4 MB',
    uploadDate: 'Feb 28',
    status: 'indexed',
  },
  {
    id: 'doc-2',
    name: 'Wine-List-2026.pdf',
    type: 'PDF',
    size: '1.1 MB',
    uploadDate: 'Feb 28',
    status: 'indexed',
  },
  {
    id: 'doc-3',
    name: 'Allergen-Matrix.xlsx',
    type: 'XLSX',
    size: '340 KB',
    uploadDate: 'Mar 1',
    status: 'processing',
  },
];

export const mockKnowledgeSources: KnowledgeSource[] = [
  {
    id: 'ks-1',
    name: 'Slab',
    icon: '\u{1F4D6}',
    workspace: 'teals-restaurant',
    spaces: ['Menu Documentation', 'Wine List'],
    pageCount: 23,
    lastSynced: '2h ago',
    status: 'connected',
  },
];

export const mockTasks: TaskDefinition[] = [
  {
    id: 'task-1',
    name: 'check_menu',
    description: 'Look up menu items, dietary info, and pricing',
    icon: '\u{1F37D}',
    inputs: [
      { name: 'query', type: 'string', required: true },
      { name: 'dietary', type: 'string[]', required: false },
    ],
    outputs: [{ name: 'items', type: 'MenuItem[]', required: true }],
  },
  {
    id: 'task-2',
    name: 'make_reservation',
    description: 'Book a table for specified date, time, and party size',
    icon: '\u{1F4C5}',
    inputs: [
      { name: 'date', type: 'string', required: true },
      { name: 'time', type: 'string', required: true },
      { name: 'party_size', type: 'number', required: true },
    ],
    outputs: [{ name: 'confirmation', type: 'Reservation', required: true }],
  },
  {
    id: 'task-3',
    name: 'check_wait_time',
    description: 'Estimate current wait time for walk-in guests',
    icon: '\u{23F0}',
    inputs: [{ name: 'party_size', type: 'number', required: true }],
    outputs: [{ name: 'wait_minutes', type: 'number', required: true }],
  },
];

export const mockTools: ConnectedTool[] = [
  {
    id: 'tool-1',
    name: 'Booking API',
    icon: '\u{1F310}',
    usedBy: ['make_reservation'],
    status: 'connected',
  },
];

export const mockFleetConnections: FleetConnection[] = [
  {
    id: 'fleet-1',
    name: 'emily-menu',
    type: 'Conversational',
    description: 'Menu queries',
    initials: 'EM',
    color: 'info',
  },
  {
    id: 'fleet-2',
    name: 'emily-reservations',
    type: 'Task',
    description: 'Table bookings',
    initials: 'ER',
    color: 'warning',
  },
];

export const mockCapabilities: CapabilityToggle[] = [
  { id: 'cap-exec', label: 'Task Execution', enabled: true },
  { id: 'cap-deleg', label: 'Agent Delegation', enabled: true },
  { id: 'cap-spawn', label: 'Agent Spawning', enabled: false },
  { id: 'cap-voice', label: 'Voice Output', enabled: false },
  { id: 'cap-struct', label: 'Structured Output', enabled: true },
];

export const mockConversationStyle = ['Formal', 'Courteous', 'Concise'];

export const mockSpecialistPrompts: SpecialistPrompt[] = [
  {
    agentName: 'emily-menu',
    displayName: 'Emily Menu',
    prompt:
      'You are emily-menu, a specialist agent for menu-related queries. Answer questions about dishes, ingredients, allergens, dietary options, and pricing. Be concise and accurate. Refer to the knowledge base for current menu items.',
  },
  {
    agentName: 'emily-reservations',
    displayName: 'Emily Reservations',
    prompt:
      'You are emily-reservations, a specialist for table bookings. Handle reservation requests, modifications, and cancellations. Always confirm date, time, party size, and special requests before confirming a booking.',
  },
];

export const mockOutputFormat: OutputFormatInfo = {
  type: 'structured',
  schemaFile: 'models/response/restaurant_response.json',
  schemaFields: [
    { name: 'message', type: 'string', required: true },
    { name: 'items', type: 'MenuItem[]', required: false },
    { name: 'confirmation', type: 'Reservation', required: false },
    { name: 'wait_minutes', type: 'number', required: false },
  ],
};

export const suggestionChips: SuggestionChipSet = {
  behavior: [
    { label: 'Edit Prompt', icon: '\u{1F4DD}' },
    { label: 'Change Model', icon: '\u2699' },
    { label: 'Adjust Temperature', icon: '\u{1F321}' },
  ],
  knowledge: [
    { label: 'Upload Documents', icon: '\u{1F4C4}' },
    { label: 'Connect Source', icon: '\u{1F517}' },
    { label: 'Set Sync Schedule', icon: '\u{1F504}' },
  ],
  tasks: [
    { label: 'Add Task', icon: '\u{1F4CB}' },
    { label: 'Define Schema', icon: '\u{1F9E9}' },
    { label: 'Enable Delegation', icon: '\u{1F465}' },
  ],
  prompts: [
    { label: 'Edit System Prompt', icon: '\u{1F4DD}' },
    { label: 'Add Specialist Prompt', icon: '\u{1F465}' },
    { label: 'Change Output Format', icon: '\u{1F4C4}' },
  ],
};

export function buildMockChatMessages(agentName: string): ConfigChatMessage[] {
  return [
    {
      id: 'msg-1',
      role: 'ai',
      content: `I can help you configure **${agentName}** across behavior, knowledge, and tasks. What would you like to change?\n\n- Make ${agentName}'s tone more professional\n- Connect our Confluence docs as a knowledge source\n- Add a task for checking wait times\n- Change the model to Claude 4 Opus`,
    },
    {
      id: 'msg-2',
      role: 'user',
      content: `Connect our Slab workspace so ${agentName} can reference the menu documentation`,
    },
    {
      id: 'msg-3',
      role: 'ai',
      content: `I'll connect your Slab workspace as a knowledge source for ${agentName}:`,
      diffs: [
        {
          header: `agents/${agentName.toLowerCase()}/knowledge.json \u2014 sources`,
          lines: [
            { type: 'added', text: '+ "slab": {' },
            { type: 'added', text: '+   "workspace": "teals-restaurant",' },
            { type: 'added', text: '+   "spaces": ["Menu Documentation", "Wine List"],' },
            { type: 'added', text: '+   "sync": "daily"' },
            { type: 'added', text: '+ }' },
          ],
        },
      ],
    },
    {
      id: 'msg-4',
      role: 'user',
      content: 'Also increase the temperature to 0.8 and make the tone more formal',
    },
    {
      id: 'msg-5',
      role: 'ai',
      content: 'Two changes \u2014 adjusting temperature and updating the prompt tone:',
      diffs: [
        {
          header: `agents/${agentName.toLowerCase()}/config.json \u2014 settings`,
          lines: [
            { type: 'removed', text: '- "temperature": 0.5' },
            { type: 'added', text: '+ "temperature": 0.8' },
          ],
        },
        {
          header: `agents/${agentName.toLowerCase()}/prompt.json \u2014 system prompt`,
          lines: [
            { type: 'removed', text: `- You are ${agentName}, a friendly restaurant assistant.` },
            { type: 'added', text: `+ You are ${agentName}, a professional restaurant concierge.` },
            { type: 'added', text: '+ Address all guests as "valued guest" and maintain a formal, courteous tone.' },
          ],
        },
      ],
    },
  ];
}
